import * as Sentry from '@sentry/react';
import type { JobsFieldSet } from '@rogoag/airtable/crm';
import { LocalAtRecord } from '../db';
import logger from '../logger';
import type Mission from '../db/MissionClass';

/**
 * Jobs field (fldpueNi3FsFCZbvq) that ops uses to see when an operator actually
 * began pulling soil on a field. It postdates the pinned @rogoag/airtable
 * v2.24.0 types, but JobsFieldSet inherits Airtable's string index signature so
 * get/set still type-check.
 */
export const STARTED_SAMPLING_FIELD = 'Started Sampling';

/**
 * Stamps `Started Sampling` on the mission's Job the first time a real core is
 * pulled. Called from CoreCompletion once a core has been attributed to a sample
 * - test cores and failed cores never reach it.
 */
class StartedSamplingRecorder {
  async recordRealCorePulled(mission: Mission, pulledAtMs: number) {
    if (!mission.job_id) {
      return;
    }

    // Deliberately the local cache only, not Mission.get_airtable_job_record():
    // that falls back to a network search, and this runs in the core completion
    // path where the operator is regularly offline.
    const jobRecord = LocalAtRecord.getRecord<JobsFieldSet>('Jobs', mission.job_id);

    if (!jobRecord) {
      await logger.log('STARTED_SAMPLING', `No cached job record for ${mission.job_id}, skipping`);

      return;
    }

    const stamped = !!jobRecord.get(STARTED_SAMPLING_FIELD);
    // A stamp that is still in modified_fields was set locally but never made it
    // to Airtable (offline). syncMulti clears the set on success, so this is how
    // we know to retry - and because the value is already on the record, the
    // retry keeps the FIRST core's timestamp.
    const pendingSync =
      jobRecord.modified_fields instanceof Set && jobRecord.modified_fields.has(STARTED_SAMPLING_FIELD);

    if (stamped && !pendingSync) {
      return;
    }

    if (!stamped) {
      await jobRecord.set(STARTED_SAMPLING_FIELD, new Date(pulledAtMs).toISOString());
    }

    try {
      await jobRecord.sync();
    } catch (err) {
      // Leave the stamp pending - the next real core retries it.
      await logger.log('STARTED_SAMPLING', `Failed to sync ${mission.job_id}: ${err}`);
      Sentry.captureException(err);
    }
  }
}

export default StartedSamplingRecorder;
